import { useEffect, useState } from "react";
import { ORDER_VERBS, orderText, squadColor, squadTag, type OrderKind } from "@/data/squads.ts";
import { currentOrderFor } from "@/session/sessionDoc.ts";
import type { SessionApi } from "@/session/useSession.ts";

interface OrderPanelProps {
  readonly session: SessionApi;
  /** Callout reference currently under the cursor. */
  readonly cursorPlace: string | null;
  /** Callout reference the commander last clicked on the map. */
  readonly pickedPlace: string | null;
  readonly onClearPick: () => void;
}

/**
 * Orders.
 *
 * Two completely different jobs behind one component, because they are two
 * completely different people:
 *
 *   A squad member has exactly one question — what am I doing right now. So
 *   they get one large, unmissable line. The lead gets a Done button. That
 *   is it — nothing to acknowledge, because this is a second screen and a
 *   squad that is fighting does not alt-tab to click a button.
 *
 *   The commander has to keep six squads tasked without typing, and plans
 *   change constantly, so an order is something you adjust rather than replace.
 */
export function OrderPanel({
  session,
  cursorPlace,
  pickedPlace,
  onClearPick,
}: OrderPanelProps): React.ReactElement {
  if (!session.isCommander) {
    return <SquadOrderStrip session={session} compact={false} />;
  }

  return (
    <>
      {/* A commander who is also in a squad still sees that squad's order, so
          a lead-who-is-commander can mark it done. The console below only
          issues and cancels. */}
      {session.mySquad && session.myOrder && <SquadOrderStrip session={session} compact />}
      <CommanderPanel
        session={session}
        cursorPlace={cursorPlace}
        pickedPlace={pickedPlace}
        onClearPick={onClearPick}
      />
    </>
  );
}

/**
 * What a squad sees: the one order they are working to, big and unmissable,
 * and — for the lead only — a way to mark it done. Nothing to confirm,
 * nothing to click just to prove you read it; this is a second screen, and
 * a squad in a firefight has no hand free for that.
 */
function SquadOrderStrip({
  session,
  compact,
}: {
  readonly session: SessionApi;
  /** Tighter layout when stacked above the commander's console. */
  readonly compact: boolean;
}): React.ReactElement {
  const { myOrder, mySquad } = session;

  if (!mySquad) {
    return (
      <div className="flex items-center gap-2 border-t border-border-subtle bg-surface px-3 py-2">
        <span className="text-xs text-text-muted">
          Join a squad to receive orders from your commander.
        </span>
      </div>
    );
  }

  if (!myOrder) {
    return (
      <div className="flex items-center gap-2 border-t border-border-subtle bg-surface px-3 py-2">
        <SquadBadge squadId={mySquad} session={session} />
        <span className="text-xs text-text-muted">No orders. Hold and await tasking.</span>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 border-t-2 bg-surface-raised px-3 ${compact ? "py-1.5" : "py-2"}`}
      style={{ borderColor: squadColor(mySquad) }}
    >
      <SquadBadge squadId={mySquad} session={session} />
      {compact && (
        <span className="text-[10px] font-medium tracking-wide text-text-muted uppercase">
          Your squad
        </span>
      )}
      <span
        className={`numeric font-bold tracking-wide text-text-primary ${compact ? "text-sm" : "text-base"}`}
      >
        {orderText(myOrder.kind, myOrder.place || undefined, myOrder.note)}
      </span>

      {session.isSquadLead && (
        <button
          type="button"
          onClick={() => session.complete(myOrder.id)}
          className="touch-target ml-auto rounded bg-accent px-4 text-xs font-semibold text-surface"
        >
          Done
        </button>
      )}
    </div>
  );
}

/** What the commander sees: a tasking console that edits in place. */
function CommanderPanel({
  session,
  cursorPlace,
  pickedPlace,
  onClearPick,
}: {
  readonly session: SessionApi;
  readonly cursorPlace: string | null;
  readonly pickedPlace: string | null;
  readonly onClearPick: () => void;
}): React.ReactElement {
  const [squadId, setSquadId] = useState<string>("");
  const [typed, setTyped] = useState("");
  const [note, setNote] = useState("");

  // Squads are created during the session rather than existing up front, so
  // there is nothing to default to until the first one shows up — and if the
  // selected squad ever stops existing (nobody has, but belt and braces),
  // fall back to whatever squad is first rather than tasking a ghost.
  useEffect(() => {
    if (squadId && session.squads.some((squad) => squad.id === squadId)) return;
    setSquadId(session.squads[0]?.id ?? "");
  }, [session.squads, squadId]);

  const existing = currentOrderFor(session.orders, squadId);

  // Switching squads shows that squad's note rather than carrying the last one
  // across, which would silently attach it to the wrong order.
  useEffect(() => {
    setNote(existing?.note ?? "");
    setTyped("");
  }, [squadId, existing?.id, existing?.note]);

  /**
   * Target precedence: what you typed, then what you clicked, then what you
   * are merely hovering. Typing is the most deliberate act, hovering the
   * least, so they rank in that order.
   */
  const target = typed.trim() || pickedPlace || cursorPlace || "";
  const targetIsPicked = !typed.trim() && Boolean(pickedPlace);

  /**
   * A verb press means "make this the order". If the squad already has one it
   * is amended rather than replaced, so the order keeps its identity while the
   * plan changes around it.
   */
  const applyVerb = (kind: OrderKind): void => {
    if (existing) {
      session.amend(existing.id, { kind, place: target || existing.place, note });
    } else {
      if (!target) return;
      session.issue({ squadId, kind, place: target, note });
    }
    setTyped("");
    onClearPick();
  };

  /** Retarget without changing the verb. */
  const applyTarget = (): void => {
    if (!existing || !target) return;
    session.amend(existing.id, { place: target, note });
    setTyped("");
    onClearPick();
  };

  const applyNote = (): void => {
    if (!existing) return;
    session.amend(existing.id, { note });
  };

  if (session.squads.length === 0) {
    return (
      <div className="flex items-center gap-2 border-t-2 border-accent/50 bg-surface-raised px-3 py-2">
        <span className="text-[11px] font-semibold tracking-wide text-accent uppercase">
          Task
        </span>
        <span className="text-xs text-text-muted">
          No squads yet — task one as soon as a player creates it in the roster.
        </span>
      </div>
    );
  }

  return (
    <div className="border-t-2 border-accent/50 bg-surface-raised px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold tracking-wide text-accent uppercase">
          Task
        </span>

        {/* One button per squad — beats a dropdown when you are in a hurry. */}
        <div className="flex flex-wrap gap-1" role="tablist" aria-label="Squad">
          {session.squads.map((squad) => {
            const selected = squad.id === squadId;
            const tasked = currentOrderFor(session.orders, squad.id);
            const name = squad.name || squad.id;
            const color = squadColor(squad.id);
            return (
              <button
                key={squad.id}
                type="button"
                role="tab"
                aria-selected={selected}
                aria-label={
                  tasked
                    ? `${name}: ${orderText(tasked.kind, tasked.place || undefined, tasked.note)}`
                    : `${name}: no order`
                }
                onClick={() => setSquadId(squad.id)}
                title={
                  tasked
                    ? `${name}: ${orderText(tasked.kind, tasked.place || undefined, tasked.note)}`
                    : `${name}: no order`
                }
                className="numeric relative h-7 w-7 rounded text-[11px] font-bold transition-opacity"
                style={
                  selected
                    ? { backgroundColor: color, color: "oklch(0.16 0.01 250)" }
                    : { color, opacity: 0.4 }
                }
              >
                {session.squadTags.get(squad.id) ?? squadTag(name)}
                {/* A dot marks a squad that already has something to do, so the
                    commander can see at a glance who is idle. */}
                {tasked && (
                  <span
                    aria-hidden
                    className="absolute right-0.5 bottom-0.5 h-1 w-1 rounded-full"
                    style={{
                      backgroundColor: selected
                        ? "oklch(0.16 0.01 250)"
                        : "var(--color-text-primary)",
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-1">
          <input
            value={typed}
            onChange={(event) => setTyped(event.target.value.toUpperCase())}
            onKeyDown={(event) => {
              if (event.key === "Enter") applyTarget();
            }}
            placeholder={target || "Click the map, or type D5-7"}
            aria-label="Target grid reference"
            className="numeric w-44 rounded border px-2 py-1 text-xs text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            style={{
              borderColor: targetIsPicked
                ? "var(--color-accent)"
                : "var(--color-border-subtle)",
              backgroundColor: "var(--color-surface-sunken)",
            }}
          />
          {targetIsPicked && (
            <button
              type="button"
              onClick={onClearPick}
              title="Clear the picked target"
              className="rounded px-1 text-[10px] text-text-muted hover:text-danger"
            >
              &times;
            </button>
          )}
          {existing && target && target !== existing.place && (
            <button
              type="button"
              onClick={applyTarget}
              title="Move this order to the new target"
              className="rounded border border-accent px-2 py-1 text-[11px] text-accent"
            >
              Retarget
            </button>
          )}
        </div>

        <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Order verb">
          {ORDER_VERBS.map((verb) => {
            const active = existing?.kind === verb.id;
            return (
              <button
                key={verb.id}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => applyVerb(verb.id)}
                disabled={!existing && !target}
                title={verb.blurb}
                className="rounded border px-2 py-1 text-[11px] transition-colors disabled:cursor-not-allowed disabled:opacity-30"
                style={
                  active
                    ? {
                        borderColor: "var(--color-accent)",
                        color: "var(--color-accent)",
                        backgroundColor: "var(--color-accent-muted)",
                      }
                    : {
                        borderColor: "var(--color-border-subtle)",
                        color: "var(--color-text-secondary)",
                      }
                }
              >
                {verb.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
        <span className="text-text-muted">
          {session.squads.find((squad) => squad.id === squadId)?.name || squadId}:
        </span>

        {existing ? (
          <>
            <span className="numeric font-semibold text-text-primary">
              {orderText(existing.kind, existing.place || undefined, existing.note)}
            </span>

            <input
              value={note}
              onChange={(event) => setNote(event.target.value)}
              onBlur={applyNote}
              onKeyDown={(event) => {
                if (event.key === "Enter") applyNote();
              }}
              maxLength={80}
              placeholder="Add a note"
              aria-label="Order note"
              className="w-56 rounded border border-border-subtle bg-surface-sunken px-2 py-0.5 text-[11px] text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none"
            />

            <button
              type="button"
              onClick={() => session.cancel(existing.id)}
              className="rounded px-2 py-0.5 text-[11px] text-text-muted transition-colors hover:text-danger"
            >
              Cancel order
            </button>
          </>
        ) : (
          <span className="text-text-muted">No current order. Pick a target, then a verb.</span>
        )}
      </div>
    </div>
  );
}

function SquadBadge({
  squadId,
  session,
}: {
  readonly squadId: string;
  readonly session: SessionApi;
}): React.ReactElement {
  const squad = session.squads.find((entry) => entry.id === squadId);
  const name = squad?.name || squad?.id;
  const tag = squad ? (session.squadTags.get(squad.id) ?? squadTag(name ?? "")) : "?";
  return (
    <span
      className="numeric flex h-6 w-6 shrink-0 items-center justify-center rounded text-xs font-bold"
      style={{ backgroundColor: squadColor(squadId), color: "oklch(0.16 0.01 250)" }}
      title={name ?? "Unassigned"}
    >
      {tag}
    </span>
  );
}
